import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const bytesToMb = (bytes) => (bytes == null ? '' : Math.round(bytes / (1024 * 1024)));
const formatSpace = (mb) => { if (mb == null || mb === '') return '—'; const n = Number(mb); if (isNaN(n)) return '—'; return n >= 1024 ? (n / 1024).toFixed(1) + ' GB' : n + ' MB'; };
const formatInr = (n) => (n != null && !isNaN(n) ? `₹ ${Number(n).toLocaleString('en-IN')}` : '—');
const inputCls = 'w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 bg-slate-50 focus:bg-white transition-all';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', space_mb: '', duration_days: '', amount_inr: '', is_active: true });
  const [editingPlan, setEditingPlan] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/subscription-plans`);
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setPlans([]); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    const name = (formData.name || '').trim();
    const spaceMb = parseInt(String(formData.space_mb).replace(/\D/g, ''), 10);
    const durationDays = parseInt(String(formData.duration_days).replace(/\D/g, ''), 10);
    const amountInr = formData.amount_inr !== '' && formData.amount_inr != null ? parseFloat(String(formData.amount_inr).replace(/[^\d.]/g, '')) : null;
    if (!name) { setError('Plan name is required'); setSaving(false); return; }
    if (isNaN(spaceMb) || spaceMb < 0) { setError('Valid space (MB) is required'); setSaving(false); return; }
    if (isNaN(durationDays) || durationDays < 1) { setError('Duration must be at least 1 day'); setSaving(false); return; }
    try {
      if (editingPlan) {
        await axios.put(`${API_URL}/subscription-plans/${editingPlan.id}`, { name, space_mb: spaceMb, duration_days: durationDays, amount_inr: amountInr, is_active: formData.is_active });
        setPlans(plans.map((p) => p.id === editingPlan.id ? { ...p, name, space_bytes: spaceMb * 1024 * 1024, duration_days: durationDays, amount_inr: amountInr, is_active: formData.is_active ? 1 : 0 } : p));
      } else {
        const res = await axios.post(`${API_URL}/subscription-plans`, { name, space_mb: spaceMb, duration_days: durationDays, amount_inr: amountInr });
        setPlans([res.data, ...plans]);
      }
      setShowModal(false); setEditingPlan(null); setFormData({ name: '', space_mb: '', duration_days: '', amount_inr: '', is_active: true });
    } catch (err) { setError(err.response?.data?.error || 'Failed to save plan'); } finally { setSaving(false); }
  };

  const handleToggleActive = async (plan) => {
    const newActive = !(plan.is_active === 1 || plan.is_active === true);
    if (newActive === false && !window.confirm(`Deactivate plan "${plan.name}"?`)) return;
    try {
      await axios.put(`${API_URL}/subscription-plans/${plan.id}`, { is_active: newActive });
      setPlans(plans.map((p) => p.id === plan.id ? { ...p, is_active: newActive ? 1 : 0 } : p));
    } catch (err) { alert(err.response?.data?.error || 'Failed to update status'); }
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({ name: plan.name, space_mb: String(bytesToMb(plan.space_bytes) || ''), duration_days: String(plan.duration_days || ''), amount_inr: plan.amount_inr != null ? String(plan.amount_inr) : '', is_active: !!(plan.is_active === 1 || plan.is_active === true) });
    setError(''); setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-sm text-slate-500 mt-0.5">{plans.length} plan{plans.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setEditingPlan(null); setFormData({ name: '', space_mb: '', duration_days: '', amount_inr: '', is_active: true }); setError(''); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-md active:scale-95 transition-all"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
          Add plan
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {plans.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-400 text-sm">No plans yet. Add a plan to offer subscriptions to users.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  {['Name', 'Amount (INR)', 'Space', 'Duration', 'Status', ''].map((h, i) => (
                    <th key={i} className={`px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {plans.map((plan) => {
                  const active = plan.is_active === 1 || plan.is_active === true;
                  return (
                    <tr key={plan.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{plan.name}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{formatInr(plan.amount_inr)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{formatSpace(bytesToMb(plan.space_bytes))}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{plan.duration_days} days</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          {active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(plan)} className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button onClick={() => handleToggleActive(plan)} className={`p-1.5 rounded-lg transition-colors ${active ? 'text-rose-500 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'}`} title={active ? 'Deactivate' : 'Activate'}>
                            {active
                              ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                              : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            }
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowModal(false); setError(''); }} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-4">{editingPlan ? 'Edit plan' : 'Add plan'}</h3>
            {error && <p className="text-rose-600 text-sm mb-3">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-3">
              {[
                { label: 'Plan name', key: 'name', placeholder: 'e.g. Starter Plan' },
                { label: 'Space (MB)', key: 'space_mb', placeholder: 'e.g. 10240 for 10 GB', numeric: true },
                { label: 'Duration (days)', key: 'duration_days', placeholder: 'e.g. 90', numeric: true },
                { label: 'Amount (₹ INR)', key: 'amount_inr', placeholder: 'e.g. 999', decimal: true },
              ].map(({ label, key, placeholder, numeric, decimal }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                  <input
                    type="text"
                    inputMode={numeric ? 'numeric' : decimal ? 'decimal' : 'text'}
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    className={inputCls}
                    placeholder={placeholder}
                  />
                </div>
              ))}
              {editingPlan && (
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                  <span className="text-sm text-slate-700">Active (available for new subscriptions)</span>
                </label>
              )}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
